/**
 * Geschosse als Zähler mit Gebäude-Symbol (xlsx Z. 7, Punkt 1).
 *
 * Vorher zwei nackte Zahlenfelder nebeneinander („Über Erdgleiche" / „Unter
 * Erdgleiche") — im öffentlichen Formular war weder erkennbar, was gemeint ist,
 * noch ließ sich die Zahl auf dem Handy bequem ändern. Jetzt eine Zeile je
 * Angabe: Bild, große Zahl, Zähler mit „+" oben und „−" unten.
 *
 * **Der Vorschlag (3 bzw. 0) steht als Platzhalter, nicht als Wert.** Er wird
 * beim Rendern also *nicht* in die Antworten geschrieben. Täte er das, meldete
 * das Fenster ungespeicherte Änderungen, obwohl niemand etwas angefasst hat —
 * genau der Fehler, der in #1314 an der Gesamtfläche behoben wurde. Nebenbei
 * löst der Platzhalter ein zweites Problem: das Feld ist technisch leer, eine
 * getippte Ziffer hängt sich also nicht an die vorgeschlagene an.
 */
import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { ILLU } from "./illustrationTokens";
import GrossZahlEingabe from "./GrossZahlEingabe";

/** Höchstwert des Zählers — zwei Stellen, wie der Ziffernfilter des Felds. */
const MAX = 99;

const useStyles = makeStyles({
  // Die Breite ist gedeckelt, damit der Zähler neben der Zahl steht und nicht
  // am rechten Rand des Formulars klebt — und damit beide Zeilen trotz
  // unterschiedlich langer Beschriftung dieselbe Zählerspalte haben.
  zeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    maxWidth: "420px",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  bild: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: ILLU.headerBg,
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "8px",
    paddingRight: "8px",
    ...shorthands.borderRadius("10px"),
  },
  svg: {
    width: "68px",
    height: "54px",
    "@media (min-width: 480px)": {
      width: "88px",
      height: "70px",
    },
  },
  mitte: {
    flexGrow: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
  },
  beschriftung: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  zaehler: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  // 44 px Kantenlänge: das ist das Touch-Ziel aus den WCAG-Empfehlungen —
  // darunter trifft auf dem Handy niemand zuverlässig das richtige Zeichen.
  knopf: {
    width: "44px",
    height: "44px",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.5rem",
    lineHeight: 1,
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    transitionProperty: "background-color",
    transitionDuration: "120ms",
    ":hover": { backgroundColor: "#f5eef6" },
    ":focus-visible": {
      ...shorthands.outline("2px", "solid", BRAND.purple),
      outlineOffset: "2px",
    },
  },
});

/**
 * Gebäude-Silhouette mit gezählten Geschossen. Gleiche Bildsprache wie die
 * übrigen Illustrationen des Schritts: viewBox `0 0 100 80`, Farben aus `ILLU`.
 *
 * - `ueber`: Grundlinie unten (y=74), drei Geschosse darüber.
 * - `unter`: Grundlinie oben (y=26), zwei Kellergeschosse darunter; der
 *   angeschnittene Baukörper über der Linie ist gestrichelt und gedämpft, damit
 *   klar bleibt, dass er nicht mitgezählt wird.
 */
const GeschossIcon = ({
  variante,
  className,
}: {
  variante: "ueber" | "unter";
  className?: string;
}) =>
  variante === "ueber" ? (
    <svg
      className={className}
      width="100"
      height="80"
      viewBox="0 0 100 80"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 74h92" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="M22 22 50 6l28 16z"
        fill={ILLU.roofFill}
        stroke={ILLU.roofStroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <rect x="28" y="22" width="44" height="52" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.5" />
      <path d="M28 39h44M28 56h44" stroke={ILLU.jointStroke} strokeWidth="1" />
      <g fill={ILLU.glassFill} stroke={ILLU.jointStroke} strokeWidth="0.8">
        <rect x="34" y="27" width="10" height="8" />
        <rect x="56" y="27" width="10" height="8" />
        <rect x="34" y="44" width="10" height="8" />
        <rect x="56" y="44" width="10" height="8" />
        <rect x="34" y="61" width="10" height="8" />
        <rect x="56" y="61" width="10" height="8" />
      </g>
    </svg>
  ) : (
    <svg
      className={className}
      width="100"
      height="80"
      viewBox="0 0 100 80"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="28"
        y="10"
        width="44"
        height="16"
        fill={ILLU.headerBg}
        stroke={ILLU.jointStroke}
        strokeWidth="1"
        strokeDasharray="4 3"
      />
      <path d="M4 26h92" stroke={ILLU.roofStroke} strokeWidth="1.5" strokeLinecap="round" />
      <g stroke={ILLU.jointStroke} strokeWidth="1">
        <path d="M8 31 14 26M17 31 23 26M77 31 83 26M86 31 92 26" />
      </g>
      <rect x="28" y="26" width="44" height="48" fill={ILLU.wallFill} stroke={ILLU.wallStroke} strokeWidth="1.5" />
      <path d="M28 50h44" stroke={ILLU.jointStroke} strokeWidth="1" />
      <g fill={ILLU.glassFill} stroke={ILLU.jointStroke} strokeWidth="0.8">
        <rect x="34" y="33" width="10" height="8" />
        <rect x="56" y="33" width="10" height="8" />
        <rect x="34" y="57" width="10" height="8" />
        <rect x="56" y="57" width="10" height="8" />
      </g>
    </svg>
  );

type Props = {
  /** Rohwert des Answer-Keys (`geschosseUeber` bzw. `geschosseUnter`). */
  value: unknown;
  /** Gespeichert wird weiterhin ein String — die Speicherform bleibt gleich. */
  onChange: (naechste: string) => void;
  /** Angezeigter Vorschlag, solange nichts erfasst ist. Wird nie geschrieben. */
  vorschlag: number;
  variante: "ueber" | "unter";
  label: string;
};

const GeschossStepper = ({ value, onChange, vorschlag, variante, label }: Props) => {
  const styles = useStyles();
  const text = typeof value === "number" ? String(value) : String(value ?? "");

  // Solange nichts erfasst ist, rechnet der Zähler ab dem Vorschlag: „+" führt
  // dann zu 4 bzw. 1, „−" zu 2 bzw. 0. Ein unlesbarer Bestandswert („k. A.")
  // fällt ebenfalls auf den Vorschlag zurück.
  const basis = /^\d+$/.test(text) ? Number(text) : vorschlag;
  const setze = (naechste: number) => {
    const geklemmt = String(Math.min(Math.max(naechste, 0), MAX));
    if (geklemmt !== text) onChange(geklemmt);
  };

  return (
    <div className={styles.zeile}>
      <span className={styles.bild}>
        <GeschossIcon variante={variante} className={styles.svg} />
      </span>
      <span className={styles.mitte}>
        <GrossZahlEingabe
          value={text}
          onChange={onChange}
          ariaLabel={label}
          platzhalter={String(vorschlag)}
          maxLen={2}
          kompakt
        />
        <span className={styles.beschriftung}>{label}</span>
      </span>
      <span className={styles.zaehler}>
        <button
          type="button"
          className={styles.knopf}
          aria-label={`Ein Geschoss mehr — ${label}`}
          onClick={() => setze(basis + 1)}
        >
          +
        </button>
        <button
          type="button"
          className={styles.knopf}
          aria-label={`Ein Geschoss weniger — ${label}`}
          onClick={() => setze(basis - 1)}
        >
          −
        </button>
      </span>
    </div>
  );
};

export default GeschossStepper;
